import { useEffect, useState } from "react";
import { api as defaultApi } from "../../api";

/**
 * Public player data (API-Football): summary, season-by-competition, match log.
 * The provider fixture id is resolved from the CSB fixture so the "this match"
 * row is matched on the id the game log actually uses.
 */
export function usePlayerCore({ api = defaultApi, playerId, fixtureId }) {
  const [state, setState] = useState({ loading: true, error: "", summary: null, games: [], teams: { home: "", away: "" }, providerFixtureId: null });
  useEffect(() => {
    if (!playerId || !fixtureId) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const get = (url, params) => api.get(url, { params }).then((r) => r.data);
    get("/football/player/summary", { fixture_id: fixtureId, player_id: playerId }).then(async summary => {
      if (["pitchapi", "mixed"].includes(summary?.source)) {
        return [summary, {home_team:summary.team?.name || "",away_team:""}, {games:summary.games || []}, summary.provider_fixture_id];
      }
      const [season,log,providerFixtureId] = await Promise.all([
        get("/football/season-players", { fixture_id: fixtureId }).catch(() => null),
        get("/football/player/game-log", { fixture_id: fixtureId, player_id: playerId, last: 40 }).catch(() => ({ games: [] })),
        api.get(`/api/fixtures/id/${encodeURIComponent(fixtureId)}/json`).then((r) => r.data?.fixture?.provider_fixture_id ?? null).catch(() => null),
      ]);
      return [summary,season,log,providerFixtureId];
    })
      .then(([summary, season, log, providerFixtureId]) => {
        if (!alive) return;
        setState({
          loading: false,
          error: "",
          summary,
          games: log?.games || [],
          teams: { home: season?.home_team || "", away: season?.away_team || "" },
          providerFixtureId,
        });
      })
      .catch(() => alive && setState((s) => ({ ...s, loading: false, error: "Player details are unavailable right now." })));
    return () => {
      alive = false;
    };
  }, [api, playerId, fixtureId]);
  return state;
}

/**
 * Private-research analytics (PitchAPI). Only fetched for administrators; any
 * failure or missing identity mapping leaves `data` null so callers fall back
 * to the public API-Football view instead of showing zeroes.
 */
export function useIntelligence({ api = defaultApi, kind, identity, namespace = "api_football", enabled, filters, compareId = "" }) {
  const [state, setState] = useState({ data: null, comparison: null, loading: false, error: "" });
  const key = JSON.stringify(filters || {});
  useEffect(() => {
    if (!enabled || !identity) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const params = { namespace, ...JSON.parse(key) };
    const url = (id) => `/api/football/intelligence/${kind}/${encodeURIComponent(id)}`;
    Promise.all([
      api.get(url(identity), { params }),
      compareId ? api.get(url(compareId), { params: { ...params, namespace: "pitchapi" } }) : Promise.resolve(null),
    ])
      .then(([r, c]) => alive && setState({ data: r.data, comparison: c?.data || null, loading: false, error: "" }))
      .catch((e) => alive && setState({ data: null, comparison: null, loading: false, error: e.response?.data?.detail || "Detailed analytics unavailable" }));
    return () => {
      alive = false;
    };
  }, [api, kind, identity, namespace, enabled, key, compareId]);
  return state;
}
